import React, { createContext, useContext, useState, useEffect, useCallback, useLayoutEffect, useMemo } from 'react';
import { useApplicationContext } from '../../../utils/applicationContext';

interface ICitiesContextType {
  activeCity: string;
  setActiveCity: (city: string) => void;
}

// Создаем контекст с дефолтными значениями
const CitiesContext = createContext<ICitiesContextType>({
  activeCity: '',
  setActiveCity: () => {},
});

interface ICitiesProviderProps {
  children: React.ReactNode;
  defaultCity?: string;
}

/**
 * Провайдер, отслеживающий активный город и обновляющий видимость элементов с data-city
 * @param children
 * @param defaultCity
 * @constructor
 */
export const CitiesProvider: React.FC<ICitiesProviderProps> = ({ children, defaultCity = '' }) => {
  const context = useApplicationContext();

  // Функция получения города из URL (работает и на сервере)
  const getCityFromUrl = useCallback((): string => {
    if (typeof window === 'undefined') {
      // На сервере используем URL из контекста приложения
      if (context?.custom?.initialUrl) {
        const url = new URL(context.custom.initialUrl, 'https://cian.ru/'); // Добавляем базовый URL для корректного парсинга
        const urlParams = new URLSearchParams(url.search);

        return urlParams.get('city') || defaultCity;
      }

      return defaultCity;
    }

    // На клиенте используем window.location
    const urlParams = new URLSearchParams(window.location.search);

    return urlParams.get('city') || defaultCity;
  }, [defaultCity, context]);

  // Инициализируем состояние города, используя консистентную функцию для сервера и клиента
  const [activeCity, setActiveCityState] = useState<string>(getCityFromUrl());

  /**
   * Функция обновления видимости элементов с data-city
   * Дизайнеры накидывают все города в кучу, но каждому назначают data-city.
   * При смене города нужно показывать блок только для активного города.
   */
  const updateVisibility = useCallback((city: string) => {
    if (typeof document === 'undefined') {
      return;
    }

    // Находим все элементы с data-city
    const cityElements = document.querySelectorAll('[data-city]');
    cityElements.forEach(element => {
      const elementCity = element.getAttribute('data-city');
      if (elementCity === city) {
        // Показываем элементы с активным городом
        (element as HTMLElement).style.display = 'block';
      } else {
        // Скрываем все остальные
        (element as HTMLElement).style.display = 'none';
      }
    });
  }, []);

  // Функция смены активного города
  const setActiveCity = useCallback(
    (city: string) => {
      // Обновляем URL без перезагрузки страницы
      const url = new URL(window.location.href);
      if (city) {
        url.searchParams.set('city', city);
      } else {
        url.searchParams.delete('city');
      }

      window.history.pushState({}, '', url.toString());

      // Обновляем активный блок города
      updateVisibility(city);

      // Обновляем состояние активного города
      setActiveCityState(city);
    },
    [updateVisibility],
  );

  // Используем useLayoutEffect, чтобы изначально показать только активный город
  useLayoutEffect(() => {
    const initialCity = getCityFromUrl();
    updateVisibility(initialCity);
  }, [getCityFromUrl, updateVisibility]);

  // Обработчик изменений истории браузера
  useEffect(() => {
    const handlePopState = () => {
      const newCity = getCityFromUrl();

      updateVisibility(newCity);
      setActiveCityState(newCity);
    };

    // Добавляем слушатель событий истории браузера
    window.addEventListener('popstate', handlePopState);

    // Очистка при размонтировании
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [getCityFromUrl, updateVisibility]);

  // Предоставляем значения через контекст
  const contextValue = useMemo(() => ({ activeCity, setActiveCity }), [activeCity, setActiveCity]);

  return <CitiesContext.Provider value={contextValue}>{children}</CitiesContext.Provider>;
};

// Хук для использования контекста в компонентах
export const useCity = () => useContext(CitiesContext);
